/**
 * Drive one message's only client action — Approve — WP-W11. The outreach analogue of
 * `OfferStatusControls`, cut down to the one verb this API drives: **the illegal
 * action has no DOM node.** `canApproveOutreachMessage(status)` decides whether the
 * button exists at all; there is no disabled-button state, because a greyed control
 * asserts "the system will not let you" about a move that exists for nobody, and a
 * `disabled` attribute is removed in devtools.
 *
 * `approve` does not send the message — it only clears the approval gate for
 * `dispatch_outreach`, which is why a successful approve still leaves the row
 * `approved`, not `sent`: the worker evaluates the other four gates on its own
 * schedule. This control says nothing that implies otherwise.
 *
 * A stale row can still issue an approve the server refuses `409
 * WORKFORCE_INVALID_STATE_TRANSITION`; the refusal renders through the shared
 * `ApiErrorNote`, attributed to the one message it was aimed at.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { WorkforceApiError } from '../../api/errors';
import {
  canApproveOutreachMessage,
  isTerminalOutreachMessageStatus,
} from '../../recruiting/outreach';
import type { OutreachMessageStatus } from '../../api/outreach';
import { ApiErrorNote } from '../../states/ApiErrorNote';

export interface OutreachMessageControlsProps {
  readonly messageId: string;
  readonly status: OutreachMessageStatus;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly onApprove: () => void;
}

export function OutreachMessageControls({
  messageId,
  status,
  pending,
  error,
  onApprove,
}: OutreachMessageControlsProps): ReactNode {
  const { t } = useI18n();
  const canApprove = canApproveOutreachMessage(status);

  return (
    <div className="wp-board__moves" data-testid={`w11-controls-${messageId}`}>
      {canApprove ? (
        <button
          className="wp-button"
          type="button"
          data-testid={`w11-action-approve-${messageId}`}
          disabled={pending}
          onClick={onApprove}
        >
          {t('outreach.messages.action.approve')}
        </button>
      ) : (
        <p className="wp-small wp-muted" data-testid={`w11-terminal-${messageId}`}>
          {t(
            isTerminalOutreachMessageStatus(status)
              ? 'outreach.messages.status.terminal'
              : 'outreach.messages.status.awaitingDispatch',
          )}
        </p>
      )}

      {error === null ? null : (
        <ApiErrorNote
          error={error}
          subject={t('outreach.messages.subjectWord')}
          testId={`w11-action-error-${messageId}`}
        />
      )}
    </div>
  );
}
